import { useCallback, useEffect, useMemo, useState } from 'react';
import { createReference, getReferenceString, resolveId } from '@medplum/core';
import { Practitioner, PractitionerRole, Reference, Schedule, Slot } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { View } from 'react-big-calendar';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { BlockSlotEvent } from '../types';
import { getDateRange } from '../utils/common';
import { loadAvailabilityResources } from '../utils/availability';
import {
  practitionerRoleRefsForCalendarFetch,
  slotOverlapsServiceTypeFilter,
  slotToBlockCalendarEvent,
} from '../utils/blockSlots';
import { AppointmentsFilters } from './useAppointments';

export interface UseBlockSlotsOptions {
  enabled: boolean;
  date: Date;
  view: View;
  timezone: string;
  appointmentsFilters: AppointmentsFilters;
  practitionerRoles: PractitionerRole[];
  practitioners: Practitioner[];
}

export interface UseBlockSlotsResult {
  events: BlockSlotEvent[];
  loading: boolean;
  error: Error | undefined;
  refresh: () => void;
}

function getIdSuffix(reference: string): string | undefined {
  return reference.includes('/') ? reference.split('/').pop() : reference;
}

function findPractitionerRoleByActorRef(
  practitionerRoles: PractitionerRole[],
  actorRef: string
): PractitionerRole | undefined {
  const actorId = getIdSuffix(actorRef);
  return practitionerRoles.find((r) => getReferenceString(r) === actorRef || (!!actorId && r.id === actorId));
}

function resolvePractitionerRef(practitioners: Practitioner[], practitionerRefString: string): Reference<Practitioner> {
  const practitionerId = getIdSuffix(practitionerRefString);
  const practitioner = practitioners.find(
    (p) => getReferenceString(p) === practitionerRefString || (!!practitionerId && p.id === practitionerId)
  );
  return practitioner ? createReference(practitioner) : { reference: practitionerRefString };
}

function buildScheduleToPractitioner(
  schedules: Schedule[],
  practitionerRoles: PractitionerRole[],
  practitioners: Practitioner[]
): Map<string, Reference<Practitioner>> {
  const map = new Map<string, Reference<Practitioner>>();

  for (const schedule of schedules) {
    const actorRef = schedule.actor?.[0]?.reference;
    if (!actorRef) continue;

    const practitionerRefString = findPractitionerRoleByActorRef(practitionerRoles, actorRef)?.practitioner?.reference;
    if (!practitionerRefString) continue;

    const ref = resolvePractitionerRef(practitioners, practitionerRefString);
    const scheduleRef = getReferenceString(schedule) ?? (schedule.id ? `Schedule/${schedule.id}` : undefined);
    if (scheduleRef) {
      map.set(scheduleRef, ref);
    }
    if (schedule.id) {
      map.set(`Schedule/${schedule.id}`, ref);
    }
  }

  return map;
}

function getPractitionerRefForBlockSlot(
  slot: Slot,
  scheduleMap: Map<string, Reference<Practitioner>>
): Reference<Practitioner> | undefined {
  const scheduleRefString = slot.schedule ? getReferenceString(slot.schedule) : undefined;
  const scheduleRefLiteral = slot.schedule?.reference;
  const scheduleId = slot.schedule ? resolveId(slot.schedule) : undefined;

  return (
    (scheduleRefString ? scheduleMap.get(scheduleRefString) : undefined) ||
    (scheduleRefLiteral ? scheduleMap.get(scheduleRefLiteral) : undefined) ||
    (scheduleId ? scheduleMap.get(`Schedule/${scheduleId}`) : undefined)
  );
}

async function searchBlockSlotsWithServerFilters(
  medplum: ReturnType<typeof useMedplum>,
  scheduleIds: string,
  startDateIso: string,
  endDateIso: string
): Promise<Slot[]> {
  return medplum.searchResources('Slot', {
    schedule: scheduleIds,
    status: 'busy-unavailable',
    start: `lt${endDateIso}`,
    end: `gt${startDateIso}`,
    _count: '1000',
  });
}

async function searchBlockSlotsWithClientFilters(
  medplum: ReturnType<typeof useMedplum>,
  schedules: Schedule[],
  startDateIso: string,
  endDateIso: string
): Promise<Slot[]> {
  const allSlots = await medplum.searchResources('Slot', {});
  const scheduleSet = new Set(schedules.map((s) => getReferenceString(s)));
  return allSlots.filter(
    (slot) =>
      slot.status === 'busy-unavailable' &&
      !!slot.schedule &&
      scheduleSet.has(getReferenceString(slot.schedule)) &&
      !!slot.start &&
      !!slot.end &&
      new Date(slot.start) < new Date(endDateIso) &&
      new Date(slot.end) > new Date(startDateIso)
  );
}

/**
 * Fetches `Slot` resources with status `busy-unavailable` on providers' appointment-availability schedules.
 */
export function useBlockSlots({
  date,
  view,
  timezone,
  appointmentsFilters,
  practitionerRoles,
  practitioners,
  enabled,
}: UseBlockSlotsOptions): UseBlockSlotsResult {
  const medplum = useMedplum();
  const { currentUser } = useCurrentUser();

  const [slots, setSlots] = useState<Slot[]>([]);
  const [scheduleMap, setScheduleMap] = useState<Map<string, Reference<Practitioner>>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>();

  const { startDate, endDate } = useMemo(() => getDateRange(date, view, timezone), [date, view, timezone]);

  const roleRefs = useMemo(
    () => practitionerRoleRefsForCalendarFetch(practitionerRoles, appointmentsFilters),
    [practitionerRoles, appointmentsFilters]
  );

  const fetchBlockSlots = useCallback(async () => {
    if (!currentUser || !enabled) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(undefined);

      const schedules = (
        await loadAvailabilityResources(medplum, {
          roles: appointmentsFilters.roles,
          providers: roleRefs,
        })
      ).schedules;

      const schMap = buildScheduleToPractitioner(schedules, practitionerRoles, practitioners);
      setScheduleMap(schMap);

      if (schedules.length === 0) {
        setSlots([]);
        return;
      }

      const scheduleIds = schedules.map((s) => getReferenceString(s)).join(',');

      let found = await searchBlockSlotsWithServerFilters(medplum, scheduleIds, startDate, endDate);

      if (found.length === 0) {
        found = await searchBlockSlotsWithClientFilters(medplum, schedules, startDate, endDate);
      }

      const serviceFiltered = found.filter((s) => slotOverlapsServiceTypeFilter(s, appointmentsFilters.serviceTypes));
      setSlots(serviceFiltered);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch calendar blocks'));
    } finally {
      setLoading(false);
    }
  }, [
    medplum,
    currentUser,
    enabled,
    startDate,
    endDate,
    appointmentsFilters.roles,
    appointmentsFilters.serviceTypes,
    roleRefs,
    practitionerRoles,
    practitioners,
  ]);

  useEffect(() => {
    void fetchBlockSlots();
  }, [fetchBlockSlots]);

  const events = useMemo(() => {
    return slots.flatMap((slot) => {
      const practitionerRef = getPractitionerRefForBlockSlot(slot, scheduleMap);
      return practitionerRef ? [slotToBlockCalendarEvent(slot, practitionerRef, practitioners)] : [];
    });
  }, [slots, scheduleMap, practitioners]);

  const refresh = useCallback(() => {
    void fetchBlockSlots();
  }, [fetchBlockSlots]);

  return { events, loading, error, refresh };
}
